import React, { useState, useMemo, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Search, 
  Download, 
  Filter,
  ChevronUp,
  ChevronDown,
  FileDown
} from "lucide-react";

const RISK_LABELS = {
  verde: "Verde (0-3 gg)",
  medio: "Medio (4-7 gg)",
  forte: "Forte (8-14 gg)",
  molto_forte: "Molto Forte (>14 gg)",
  unknown: "N/D"
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const RiskBadge = ({ level }) => {
  const config = {
    verde: "bg-green-100 text-green-800 border-green-300",
    medio: "bg-yellow-100 text-yellow-800 border-yellow-300",
    forte: "bg-orange-100 text-orange-800 border-orange-300",
    molto_forte: "bg-red-100 text-red-800 border-red-300",
    unknown: "bg-slate-100 text-slate-800 border-slate-300"
  };
  
  return (
    <Badge className={`${config[level] || config.unknown} border text-xs`}>
      {RISK_LABELS[level] || level}
    </Badge>
  );
};

const PreventiviTable = ({ preventivi = [], filters = { sedi: [], tcs: [] }, sessionId, token }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSede, setFilterSede] = useState("all");
  const [filterTC, setFilterTC] = useState("all");
  const [filterRisk, setFilterRisk] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "data_creazione", direction: "desc" });
  const tableRef = useRef(null);

  // Filter and sort data
  const filteredData = useMemo(() => {
    let data = [...preventivi];
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(p => 
        p.soggetto?.toLowerCase().includes(term) ||
        p.tc?.toLowerCase().includes(term) ||
        p.codice_commessa?.toLowerCase().includes(term) ||
        p.indirizzo?.toLowerCase().includes(term)
      );
    }
    
    // Sede filter
    if (filterSede && filterSede !== "all") {
      data = data.filter(p => p.sede === filterSede);
    }
    
    // TC filter
    if (filterTC && filterTC !== "all") {
      data = data.filter(p => p.tc === filterTC);
    }
    
    // Risk filter
    if (filterRisk && filterRisk !== "all") {
      data = data.filter(p => p.risk_level === filterRisk);
    }
    
    // Sort
    if (sortConfig.key) {
      data.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        
        if (sortConfig.key === "valore_preventivo") {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        } else if (sortConfig.key === "days_old") {
          aVal = parseInt(aVal) || 0;
          bVal = parseInt(bVal) || 0;
        }
        
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    
    return data;
  }, [preventivi, searchTerm, filterSede, filterTC, filterRisk, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc" ? 
      <ChevronUp className="h-4 w-4 inline ml-1" /> : 
      <ChevronDown className="h-4 w-4 inline ml-1" />;
  };

  const exportToPDF = () => {
    const printWindow = window.open("", "_blank");
    const styles = `
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { color: #0f172a; font-size: 24px; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th { background: #0f172a; color: white; padding: 10px 8px; text-align: left; }
        td { padding: 8px; border-bottom: 1px solid #e2e8f0; }
        tr:nth-child(even) { background: #f8fafc; }
        .risk-verde { background: #dcfce7; color: #166534; padding: 2px 8px; border-radius: 4px; }
        .risk-medio { background: #fef9c3; color: #854d0e; padding: 2px 8px; border-radius: 4px; }
        .risk-forte { background: #ffedd5; color: #9a3412; padding: 2px 8px; border-radius: 4px; }
        .risk-molto_forte { background: #fee2e2; color: #991b1b; padding: 2px 8px; border-radius: 4px; }
        .summary { margin-top: 20px; padding: 15px; background: #f1f5f9; border-radius: 8px; }
        .summary h3 { margin: 0 0 10px 0; }
        @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
      </style>
    `;
    
    const totalValue = filteredData.reduce((sum, p) => sum + (p.valore_preventivo || 0), 0);
    const riskCounts = filteredData.reduce((acc, p) => {
      acc[p.risk_level] = (acc[p.risk_level] || 0) + 1;
      return acc;
    }, {});
    
    const tableRows = filteredData.map(p => `
      <tr>
        <td>${p.tc || "-"}</td>
        <td>${p.codice_commessa || "-"}</td>
        <td>${p.soggetto || "-"}</td>
        <td>${p.sede || "-"}</td>
        <td>${p.data_creazione || "-"}</td>
        <td style="text-align: right;">${formatCurrency(p.valore_preventivo || 0)}</td>
        <td><span class="risk-${p.risk_level}">${RISK_LABELS[p.risk_level] || p.risk_level}</span></td>
      </tr>
    `).join("");
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Report Preventivi - Acrobatica</title>
          ${styles}
        </head>
        <body>
          <h1>Report Preventivi Commerciali</h1>
          <p>Data: ${new Date().toLocaleDateString("it-IT")} | Totale: ${filteredData.length} preventivi</p>
          
          <table>
            <thead>
              <tr>
                <th>TC</th>
                <th>Cod. Commessa</th>
                <th>Soggetto</th>
                <th>Sede</th>
                <th>Data</th>
                <th style="text-align: right;">Valore</th>
                <th>Rischio</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          
          <div class="summary">
            <h3>Riepilogo</h3>
            <p><strong>Valore Totale:</strong> ${formatCurrency(totalValue)}</p>
            <p><strong>Distribuzione Rischio:</strong> 
              Verde: ${riskCounts.verde || 0} | 
              Medio: ${riskCounts.medio || 0} | 
              Forte: ${riskCounts.forte || 0} | 
              Molto Forte: ${riskCounts.molto_forte || 0}
            </p>
          </div>
          
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="preventivi-table-page">
      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Cerca per soggetto, TC, codice..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <Select value={filterSede} onValueChange={setFilterSede}>
              <SelectTrigger className="w-[150px]" data-testid="filter-sede">
                <SelectValue placeholder="Sede" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le Sedi</SelectItem>
                {filters.sedi?.map(sede => (
                  <SelectItem key={sede} value={sede}>{sede}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterTC} onValueChange={setFilterTC}>
              <SelectTrigger className="w-[150px]" data-testid="filter-tc">
                <SelectValue placeholder="TC" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i TC</SelectItem>
                {filters.tcs?.map(tc => (
                  <SelectItem key={tc} value={tc}>{tc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterRisk} onValueChange={setFilterRisk}>
              <SelectTrigger className="w-[180px]" data-testid="filter-risk">
                <SelectValue placeholder="Rischio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i Rischi</SelectItem>
                <SelectItem value="verde">Verde (0-3 gg)</SelectItem>
                <SelectItem value="medio">Medio (4-7 gg)</SelectItem>
                <SelectItem value="forte">Forte (8-14 gg)</SelectItem>
                <SelectItem value="molto_forte">Molto Forte (>14 gg)</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              onClick={exportToPDF}
              className="border-slate-200"
              data-testid="export-pdf-btn"
            >
              <FileDown className="h-4 w-4 mr-2" />
              Esporta PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>{filteredData.length} risultati trovati</span>
        <span>Valore totale filtrato: {formatCurrency(filteredData.reduce((sum, p) => sum + (p.valore_preventivo || 0), 0))}</span>
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto" ref={tableRef}>
            <table className="w-full text-sm" data-testid="preventivi-data-table">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th 
                    className="text-left py-3 px-4 font-semibold cursor-pointer hover:bg-slate-800 transition-colors"
                    onClick={() => handleSort("tc")}
                  >
                    TC <SortIcon columnKey="tc" />
                  </th>
                  <th className="text-left py-3 px-4 font-semibold">Cod. Commessa</th>
                  <th className="text-left py-3 px-4 font-semibold">Soggetto</th>
                  <th 
                    className="text-left py-3 px-4 font-semibold cursor-pointer hover:bg-slate-800 transition-colors"
                    onClick={() => handleSort("sede")}
                  >
                    Sede <SortIcon columnKey="sede" />
                  </th>
                  <th className="text-left py-3 px-4 font-semibold">Indirizzo</th>
                  <th 
                    className="text-left py-3 px-4 font-semibold cursor-pointer hover:bg-slate-800 transition-colors"
                    onClick={() => handleSort("data_creazione")}
                  >
                    Data <SortIcon columnKey="data_creazione" />
                  </th>
                  <th 
                    className="text-right py-3 px-4 font-semibold cursor-pointer hover:bg-slate-800 transition-colors"
                    onClick={() => handleSort("valore_preventivo")}
                  >
                    Valore <SortIcon columnKey="valore_preventivo" />
                  </th>
                  <th 
                    className="text-center py-3 px-4 font-semibold cursor-pointer hover:bg-slate-800 transition-colors"
                    onClick={() => handleSort("days_old")}
                  >
                    Giorni <SortIcon columnKey="days_old" />
                  </th>
                  <th className="text-center py-3 px-4 font-semibold">Rischio</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-12 text-center text-slate-400">
                      Nessun risultato trovato
                    </td>
                  </tr>
                ) : (
                  filteredData.map((p, i) => (
                    <tr 
                      key={p.id} 
                      className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                        p.risk_level === "molto_forte" ? "bg-red-50/30" :
                        p.risk_level === "forte" ? "bg-orange-50/30" :
                        p.risk_level === "medio" ? "bg-yellow-50/30" :
                        ""
                      }`}
                    >
                      <td className="py-3 px-4 font-medium">{p.tc}</td>
                      <td className="py-3 px-4 text-slate-600">{p.codice_commessa}</td>
                      <td className="py-3 px-4 max-w-[200px] truncate" title={p.soggetto}>{p.soggetto}</td>
                      <td className="py-3 px-4">{p.sede}</td>
                      <td className="py-3 px-4 text-slate-600 max-w-[150px] truncate" title={p.indirizzo}>{p.indirizzo}</td>
                      <td className="py-3 px-4">{p.data_creazione}</td>
                      <td className="py-3 px-4 text-right font-medium">{formatCurrency(p.valore_preventivo)}</td>
                      <td className="py-3 px-4 text-center">{p.days_old}</td>
                      <td className="py-3 px-4 text-center">
                        <RiskBadge level={p.risk_level} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PreventiviTable;
